import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Form, message } from "antd";
import { useParams } from "react-router-dom";
import apiClient from "services/apiClient";
import moment from "moment";

import { useMyContext } from "Context/MyContextProvider";
import { toLower } from "lodash";
import ChatContentFooter from "./ConversationComps/ChatContentFooter";
import ChatContentBody from "./ConversationComps/ChatContentBody";
import ChatContentHeader from "./ConversationComps/ChatContentHeader";
import { useLiveChatContext } from "./LiveChatContext";
import { DEFAULT_LOCATION_LABEL } from "./locationUtils";
import { useChatMessageSearch } from "./useChatMessageSearch";
import {
  buildReplyToFromMessage,
  resolveReplyWamid,
} from "./replyUtils";
import { decryptRestrictedMessage } from "utils/restrictedMessageCrypto";
import { appendUniqueMessage, findChatByNumber, resolveCswStateFromChat, resolveCswStateFromPayload } from "./chatUtils";

export const getInitials = (name) => {
  if (!name) return "";
  const nameParts = name?.split(" ");
  return nameParts?.length === 1
    ? nameParts[0]?.charAt(0)?.toUpperCase()
    : nameParts[0]?.charAt(0)?.toUpperCase() +
    nameParts[1]?.charAt(0)?.toUpperCase();
};
export const getColorForName = (name) => {
  const colors = ["#f56a00", "#7265e6", "#ffbf00", "#00a2ae", "#87d068"];
  const index = name?.length % colors.length || 0;
  return colors[index];
};

const Conversation = (props) => {
  const { menuState } = props;
  const {
    api,
    UserData,
    authToken,
    isSupportAgent,
    isMobile,
  } = useMyContext();
  const {
    msgList,
    fetchMessages,
    appendOptimistic,
    updateMessagesForContact,
    setActiveContact,
    loadingMessages,
    setLoadingMessages,
    chatList,
    openContactCsw,
    chatAccount,
  } = useLiveChatContext();
  const params = useParams();
  const [info, setInfo] = useState({});
  const [keywords, setKeywords] = useState([]);
  const [disable, setDisable] = useState(true);
  const [activeAgent, setActiveAgent] = useState();
  const [lastReportID, setLastReportID] = useState();
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [isOldChat, setIsOldChat] = useState(false);
  const [cswExpiresAt, setCswExpiresAt] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const { id } = params;

  // Immediately populate header details from cached chat list if available to avoid blank avatars
  useEffect(() => {
    if (!info?.name && chatList?.length > 0 && id) {
      const cachedChat = chatList.find((c) => String(c.number) === String(id));
      if (cachedChat && cachedChat.name) {
        setInfo((prev) => ({
          ...prev,
          name: cachedChat.name,
          number: cachedChat.number,
        }));
      }
    }
  }, [id, chatList, info?.name]);

  const [form] = Form.useForm();
  const lastFetchedNumberRef = useRef(null);
  const chatBodyRef = useRef(null);
  const setHasMoreRef = useRef(null);

  const messageSearch = useChatMessageSearch({
    api,
    contactId: id,
    messages: msgList,
    updateMessagesForContact,
    scrollToMessageRef: chatBodyRef,
  });

  const parentNumber = useMemo(
    () => chatAccount?.whatsapp_number ?? UserData?.whatsapp_number ?? "",
    [chatAccount?.whatsapp_number, UserData?.whatsapp_number]
  );

  const handleAssignSuccess = useCallback((payload) => {
    if (payload?.active_agent) {
      setActiveAgent(payload.active_agent);
    }
    if (payload?.system_message) {
      updateMessagesForContact(id, (prev) => appendUniqueMessage(prev, payload.system_message));
    }
  }, [id, updateMessagesForContact]);

  const applyConversationMetadata = useCallback((chats) => {
    if (!chats) {
      return;
    }

    setInfo(chats);
    setActiveAgent(chats?.active_agent);

    const lastItem = chats?.msg
      ?.filter((item) => item.from === "opposite")
      .pop();
    setLastReportID(lastItem && lastItem.id);

    const cswState = resolveCswStateFromPayload(chats, chatList, id);
    if (cswState) {
      setIsOldChat(cswState.is_expired);
      setCswExpiresAt(cswState.csw_expires_at);
    }
  }, [chatList, id]);

  const applyCswFromChatList = useCallback((contactId) => {
    const cswState = resolveCswStateFromChat(findChatByNumber(chatList, contactId));
    if (!cswState) {
      return;
    }

    setIsOldChat(cswState.is_expired);
    setCswExpiresAt(cswState.csw_expires_at);
  }, [chatList]);

  const getChats = useCallback(
    async (number, handleLoader = true, page = 1, append = false, setHasMore) => {
      const numberChanged =
        String(lastFetchedNumberRef.current) !== String(number);

      if (numberChanged) {
        setActiveAgent(null);
        setLastReportID(null);
        setInfo({});
        applyCswFromChatList(number);
        lastFetchedNumberRef.current = String(number);
      }

      const chats = await fetchMessages(
        number,
        handleLoader,
        page,
        append,
        setHasMore || setHasMoreRef.current
      );

      if (!chats) {
        return;
      }

      if (numberChanged || page === 1 || !append) {
        applyConversationMetadata(chats);
      }
    },
    [applyConversationMetadata, applyCswFromChatList, fetchMessages]
  );

  useEffect(() => {
    if (!id) {
      return;
    }

    applyCswFromChatList(id);
  }, [id, chatList, applyCswFromChatList]);

  useEffect(() => {
    if (!id || !openContactCsw) {
      return;
    }

    if (String(openContactCsw.contactId) !== String(id)) {
      return;
    }

    setIsOldChat(Boolean(openContactCsw.is_expired));
    setCswExpiresAt(openContactCsw.csw_expires_at ?? null);
  }, [id, openContactCsw]);

  const fetchKeywords = useCallback(async () => {
    try {
      const response = await apiClient.get(`${api}chatbots/keywords`);
      setKeywords(response.data?.keywords);
    } catch (error) {
      console.error("Error fetching keywords:", error);
    }
  }, [api]);

  useEffect(() => {
    fetchKeywords();
  }, [fetchKeywords]);

  // On mobile: reset window scroll to 0 when the virtual keyboard is dismissed.
  // When the keyboard opens, the browser shrinks the visual viewport and may scroll
  // the page body up. When the keyboard closes the viewport grows back but the
  // scroll position is not restored automatically, leaving the chat offset.
  useEffect(() => {
    if (!isMobile || !window.visualViewport) return;
    let lastHeight = window.visualViewport.height;
    const handleViewportResize = () => {
      const newHeight = window.visualViewport.height;
      // Keyboard closed — viewport height grew
      if (newHeight > lastHeight) {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
      lastHeight = newHeight;
    };
    window.visualViewport.addEventListener('resize', handleViewportResize);
    return () => window.visualViewport.removeEventListener('resize', handleViewportResize);
  }, [isMobile]);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    chatBodyRef.current?.scrollToBottom?.(behavior);
  }, []);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    setReplyingTo(null);
    setActiveContact(String(id));
    getChats(id);
  }, [id, getChats, setActiveContact]);

  const handleReply = useCallback((message) => {
    setReplyingTo(message);
  }, []);

  const clearReply = useCallback(() => {
    setReplyingTo(null);
  }, []);

  const replyingToPreview = useMemo(
    () => (replyingTo ? buildReplyToFromMessage(replyingTo) : null),
    [replyingTo]
  );

  const getMediaType = (fileName) => {
    const mediaTypes = {
      image: ["jpg", "jpeg", "png"],
      video: ["mp4", "3gp"],
      audio: ["mp3", "ogg", "aac", "m4a"],
      document: ["pdf"],
    };

    const extension = fileName?.split(".").pop()?.split(/[?#]/)[0];

    for (const [type, extensions] of Object.entries(mediaTypes)) {
      if (extensions?.includes(extension?.toLowerCase())) {
        return toLower(type);
      }
    }
    return null;
  };

  const MAX_SELECTED_MEDIA = 10;

  const revokePreviewUrl = useCallback((item) => {
    if (item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
  }, []);

  const updateSendDisabled = useCallback((items, hasTextOverride) => {
    const hasText =
      hasTextOverride !== undefined
        ? hasTextOverride
        : Boolean(form.getFieldValue("newMsg")?.trim());
    const hasUploading = items.some((item) => item.status === "uploading");
    const hasReadyMedia = items.some(
      (item) => item.status !== "uploading" && item.id
    );
    setDisable(hasUploading || (!hasText && !hasReadyMedia));
  }, [form]);

  const addSelectedMedia = useCallback((mediaId, name, link) => {
    if (!mediaId) {
      return false;
    }

    let added = false;
    setSelectedMedia((prev) => {
      if (prev.length >= MAX_SELECTED_MEDIA) {
        message.warning(`Maximum ${MAX_SELECTED_MEDIA} files allowed`);
        return prev;
      }

      if (prev.some((item) => String(item.id) === String(mediaId))) {
        message.info("This file is already added");
        return prev;
      }

      added = true;
      const next = [
        ...prev,
        {
          clientId: `${mediaId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          id: mediaId,
          name,
          link,
          status: "ready",
          source: "library",
        },
      ];
      updateSendDisabled(next);
      return next;
    });

    return added;
  }, [updateSendDisabled]);

  const discardChatDeviceMedia = useCallback((item) => {
    if (
      item?.source !== "device" ||
      item?.status !== "ready" ||
      !item?.id ||
      !item?.link ||
      String(item.link).startsWith("blob:")
    ) {
      return;
    }

    const parentNumber = chatAccount?.whatsapp_number ?? UserData?.whatsapp_number;

    apiClient
      .post("discard-chat-device-media", {
        media_id: String(item.id),
        media_url: String(item.link),
        parent_number: String(parentNumber ?? ""),
        child_number: String(id ?? ""),
      })
      .catch((error) => {
        console.warn("Failed to discard chat device media", error);
      });
  }, [UserData, chatAccount?.whatsapp_number, id]);

  const beginDeviceMediaUploads = useCallback((files, currentCount = 0) => {
    const pendingEntries = [];
    const newItems = [];

    for (const file of files) {
      if (currentCount + newItems.length >= MAX_SELECTED_MEDIA) {
        break;
      }

      const clientId = `pending-${Date.now()}-${newItems.length}-${Math.random().toString(36).slice(2, 7)}`;
      const previewUrl = URL.createObjectURL(file);

      newItems.push({
        clientId,
        id: null,
        name: file.name,
        link: previewUrl,
        previewUrl,
        status: "uploading",
        source: "device",
      });
      pendingEntries.push({ file, clientId });
    }

    if (!newItems.length) {
      return [];
    }

    setSelectedMedia((prev) => {
      const next = [...prev, ...newItems];
      updateSendDisabled(next);
      return next;
    });

    return pendingEntries;
  }, [updateSendDisabled]);

  const completeDeviceMediaUpload = useCallback((clientId, mediaId, name, link) => {
    setSelectedMedia((prev) => {
      const next = prev.map((item) => {
        if (item.clientId !== clientId) {
          return item;
        }

        revokePreviewUrl(item);
        return {
          ...item,
          id: mediaId,
          name: name || item.name,
          link,
          previewUrl: undefined,
          status: "ready",
          source: "device",
        };
      });
      updateSendDisabled(next);
      return next;
    });
  }, [revokePreviewUrl, updateSendDisabled]);

  const failDeviceMediaUpload = useCallback((clientId) => {
    setSelectedMedia((prev) => {
      const failedItem = prev.find((item) => item.clientId === clientId);
      revokePreviewUrl(failedItem);
      const next = prev.filter((item) => item.clientId !== clientId);
      updateSendDisabled(next);
      return next;
    });
  }, [revokePreviewUrl, updateSendDisabled]);

  const removeSelectedMedia = useCallback((clientId) => {
    let removedItem = null;

    setSelectedMedia((prev) => {
      removedItem = prev.find((item) => item.clientId === clientId) || null;
      const next = prev.filter((item) => item.clientId !== clientId);
      updateSendDisabled(next);
      return next;
    });

    if (removedItem) {
      discardChatDeviceMedia(removedItem);
      revokePreviewUrl(removedItem);
    }
  }, [discardChatDeviceMedia, revokePreviewUrl, updateSendDisabled]);

  const clearSelectedMedia = useCallback(({ discardDevice = false } = {}) => {
    let itemsToDiscard = [];

    setSelectedMedia((prev) => {
      if (discardDevice) {
        itemsToDiscard = prev.filter(
          (item) =>
            item.source === "device" &&
            item.status === "ready" &&
            item.id &&
            item.link
        );
      }
      prev.forEach(revokePreviewUrl);
      return [];
    });

    if (discardDevice) {
      itemsToDiscard.forEach(discardChatDeviceMedia);
    }

    setDisable(true);
  }, [discardChatDeviceMedia, revokePreviewUrl]);

  const patchOptimisticMessage = useCallback((tempId, patch) => {
    updateMessagesForContact(id, (prev) =>
      prev.map((msg) =>
        msg._tempId === tempId ? { ...msg, ...patch } : msg
      )
    );
  }, [id, updateMessagesForContact]);

  const handleKeywordBlockedSend = useCallback(
    async (tempId, data, chatPayload) => {
      const restrictionText = data?.error || "Message blocked by keyword restriction.";
      const originalMessage =
        (await decryptRestrictedMessage(data?.original_message)) ||
        chatPayload?.original_message ||
        chatPayload?.message ||
        "";

      patchOptimisticMessage(tempId, {
        status: "failed",
        _errorMsg: restrictionText,
        text: restrictionText,
        original_message: originalMessage,
        keyword_blocked: true,
        ...(data?.chat_id ? { id: data.chat_id } : {}),
      });

      if (data?.chat_id) {
        message.error(restrictionText);
        return;
      }

      try {
        await apiClient.post(
          `${api}new-chat`,
          {
            ...chatPayload,
            message: restrictionText,
            original_message: originalMessage,
            out_report_id: data?.out_report_id || null,
            message_id: null,
          },
          {
            headers: { Authorization: "Bearer " + authToken },
          }
        );
      } catch (persistError) {
        console.error("Failed to persist keyword-blocked message", persistError);
      }

      message.error(restrictionText);
    },
    [api, authToken, patchOptimisticMessage]
  );

  const sendInBackground = useCallback(
    (tempId, metaPayload, chatPayload) => {
      (async () => {
        const sendPayload = {
          ...metaPayload,
          report_id: chatPayload.report_id,
          chat_type: chatPayload.type,
          reply_id: chatPayload.reply_id,
          quoted_message_wamid: chatPayload.quoted_message_wamid,
        };

        try {
          const res = await apiClient.post("messaging/send", sendPayload);
          if (!res.data.ok || !res.data.wamid) {
            if (res.data?.keyword_blocked) {
              await handleKeywordBlockedSend(tempId, res.data, chatPayload);
              return;
            }

            const errMsg = res.data?.error || "Message send failed";
            patchOptimisticMessage(tempId, { status: "failed", _errorMsg: errMsg });
            message.error(errMsg);
            return;
          }

          const wamid = res.data.wamid;
          patchOptimisticMessage(tempId, {
            status: "sent",
            id: wamid,
            message_id: wamid,
          });

          const reportPayload = {
            ...chatPayload,
            message_id: wamid,
            out_report_id: res.data.out_report_id || null,
          };

          await apiClient.post(`${api}new-chat`, reportPayload, {
            headers: { Authorization: "Bearer " + authToken },
          });
        } catch (error) {
          const data = error?.response?.data;
          if (data?.keyword_blocked) {
            await handleKeywordBlockedSend(tempId, data, chatPayload);
            return;
          }

          const errMsg = data?.error || data?.message || "Failed to send message";
          patchOptimisticMessage(tempId, { status: "failed", _errorMsg: errMsg });
          message.error(errMsg);
        }
      })();
    },
    [api, authToken, handleKeywordBlockedSend, patchOptimisticMessage]
  );

  const sendSingleMessage = useCallback(
    (metaPayload, messageText, messageType, displayMeta = null, replyMeta = null) => {
      const now = new Date().toISOString();
      const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const isAudioMessage = messageType === "audio";
      const isLocationMessage = messageType === "location";
      const optimisticText = isAudioMessage
        ? ""
        : isLocationMessage
          ? (displayMeta?.display_text || displayMeta?.location_name || messageText)
          : displayMeta?.file_name || messageText;

      appendOptimistic(id, {
        _tempId: tempId,
        type: messageType || "text",
        text: optimisticText,
        file_name: displayMeta?.file_name || null,
        media_url:
          displayMeta?.media_url
          || (isAudioMessage ? messageText : null),
        latitude: displayMeta?.latitude ?? null,
        longitude: displayMeta?.longitude ?? null,
        location_name: displayMeta?.location_name || null,
        location_address: displayMeta?.location_address || null,
        from: "me",
        msgType: messageType || "text",
        status: "pending",
        id: tempId,
        message_id: null,
        reply_to: replyMeta?.reply_to ?? null,
        quoted_message_wamid: replyMeta?.quoted_message_wamid ?? null,
        mesgTime: moment(now).format("h:mm A"),
        created_at: now,
      });

      const chatPayload = {
        type: messageType,
        report_id: lastReportID,
        message: messageType === "audio"
          ? messageText || displayMeta?.media_url || ""
          : messageText,
      };
      if (displayMeta?.file_name) chatPayload.file_name = displayMeta.file_name;
      if (displayMeta?.media_url) chatPayload.media_url = displayMeta.media_url;
      if (displayMeta?.latitude != null) chatPayload.latitude = displayMeta.latitude;
      if (displayMeta?.longitude != null) chatPayload.longitude = displayMeta.longitude;
      if (displayMeta?.location_name) chatPayload.location_name = displayMeta.location_name;
      if (displayMeta?.location_address) chatPayload.location_address = displayMeta.location_address;
      if (replyMeta?.reply_id) chatPayload.reply_id = replyMeta.reply_id;

      sendInBackground(tempId, metaPayload, chatPayload);
    },
    [appendOptimistic, id, lastReportID, sendInBackground]
  );

  const buildReplyMeta = useCallback(() => {
    if (!replyingTo) {
      return null;
    }

    const replyWamid = resolveReplyWamid(replyingTo);
    if (!replyWamid) {
      return null;
    }

    return {
      reply_id: replyWamid,
      quoted_message_wamid: replyWamid,
      reply_to: buildReplyToFromMessage(replyingTo),
    };
  }, [replyingTo]);

  const onSend = (values) => {
    const replyMeta = buildReplyMeta();
    if (replyingTo && !replyMeta?.reply_id) {
      message.warning("Cannot reply to this message.");
      return;
    }

    const caption = values.newMsg?.trim() || "";

    const readyMedia = selectedMedia.filter(
      (item) => item.status !== "uploading" && item.id
    );

    if (selectedMedia.some((item) => item.status === "uploading")) {
      message.warning("Please wait for uploads to finish");
      return;
    }

    if (readyMedia.length > 0) {
      updateMessagesForContact(id, (prev) =>
        prev.filter((item) => item.msgType !== "pending_message")
      );

      for (let index = 0; index < readyMedia.length; index += 1) {
        const item = readyMedia[index];
        const mediaType = getMediaType(item.name);
        if (!mediaType) {
          message.error(`Unsupported file type: ${item.name || "file"}`);
          continue;
        }

        const mediaPayload = { id: item.id };
        if (index === 0 && caption) {
          mediaPayload.caption = caption;
        }
        if (mediaType === "document" && item.name) {
          mediaPayload.filename = item.name;
        }

        const isDocument = mediaType === "document";
        const isAudio = mediaType === "audio";
        const persistedMessage = isDocument
          ? JSON.stringify({ name: item.name, url: item.link })
          : item.link;

        sendSingleMessage(
          {
            messaging_product: "whatsapp",
            to: info?.number,
            type: mediaType,
            [mediaType]: mediaPayload,
            ...(index === 0 && replyMeta?.reply_id
              ? { context: { message_id: replyMeta.reply_id } }
              : {}),
          },
          persistedMessage,
          mediaType,
          isDocument
            ? { file_name: item.name, media_url: item.link }
            : isAudio
              ? { media_url: item.link }
              : null,
          index === 0 ? replyMeta : null
        );
      }

      form.resetFields();
      clearSelectedMedia();
      clearReply();
      scrollToBottom();
      setDisable(true);
      return;
    }

    const payload = {
      messaging_product: "whatsapp",
      to: info?.number,
      type: "text",
      text: {
        preview_url: false,
        body: values.newMsg,
      },
      ...(replyMeta?.reply_id
        ? { context: { message_id: replyMeta.reply_id } }
        : {}),
    };

    sendSingleMessage(payload, values.newMsg, "text", null, replyMeta);
    form.resetFields();
    clearReply();
    scrollToBottom();
    setDisable(true);
  };

  const sendLocationMessage = useCallback((location) => {
    const replyMeta = buildReplyMeta();
    if (replyingTo && !replyMeta?.reply_id) {
      message.warning("Cannot reply to this message.");
      return;
    }

    const label = [location.name, location.address]
      .filter(Boolean)
      .join(" - ") || DEFAULT_LOCATION_LABEL;
    const persistedMessage = JSON.stringify({
      latitude: Number(location.latitude),
      longitude: Number(location.longitude),
      name: location.name || "",
      address: location.address || "",
    });

    sendSingleMessage(
      {
        messaging_product: "whatsapp",
        to: info?.number,
        type: "location",
        location: {
          latitude: Number(location.latitude),
          longitude: Number(location.longitude),
          ...(location.name ? { name: location.name } : {}),
          ...(location.address ? { address: location.address } : {}),
        },
        ...(replyMeta?.reply_id
          ? { context: { message_id: replyMeta.reply_id } }
          : {}),
      },
      persistedMessage,
      "location",
      {
        latitude: Number(location.latitude),
        longitude: Number(location.longitude),
        location_name: location.name || "",
        location_address: location.address || "",
        display_text: label,
      },
      replyMeta
    );
    clearReply();
    scrollToBottom();
  }, [buildReplyMeta, clearReply, info?.number, replyingTo, scrollToBottom, sendSingleMessage]);

  const handleValuesChange = useCallback((changedValues) => {
    const hasText = Boolean(changedValues.newMsg?.trim());
    updateSendDisabled(selectedMedia, hasText);
  }, [selectedMedia, updateSendDisabled]);

  useEffect(() => {
    clearSelectedMedia({ discardDevice: true });
  }, [id, clearSelectedMedia]);

  return (
    <div className={`chat-content${isMobile ? ' chat-content-mobile' : ''}`}>
      <ChatContentHeader
        isSupportAgent={isSupportAgent}
        cswExpiresAt={cswExpiresAt}
        isExpired={isOldChat}
        name={info?.name}
        authToken={authToken}
        number={info.number}
        loading={loadingMessages}
        displayPhoneNumber={parentNumber}
        onRefresh={() => getChats(id)}
        api={api}
        getChats={getChats}
        activeAgent={activeAgent}
        wa_id={id}
        onAssignSuccess={handleAssignSuccess}
        messageSearch={messageSearch}
      />
      <ChatContentBody
        ref={chatBodyRef}
        setLoading={setLoadingMessages}
        messages={msgList}
        loading={loadingMessages}
        keywords={keywords}
        contactId={id}
        contactName={info?.name}
        number={info.number}
        onLoadMore={(newMessages) => updateMessagesForContact(id, () => newMessages)}
        onUpdateMessage={(messageId, patch) =>
          updateMessagesForContact(id, (prev) =>
            prev.map((item) =>
              item.id === messageId ? { ...item, ...patch } : item
            )
          )
        }
        getChats={getChats}
        activeSearchMatchId={messageSearch.activeMatch?.id || null}
        searchHighlightQuery={messageSearch.debouncedQuery}
        onReply={handleReply}
        parentNumber={parentNumber}
        onRegisterSetHasMore={(fn) => {
          setHasMoreRef.current = fn;
        }}
      />
      <ChatContentFooter
        form={form}
        menuState={menuState}
        number={info.number}
        contactName={info?.name}
        replyingTo={replyingToPreview}
        onCancelReply={clearReply}
        setDisable={setDisable}
        onSend={onSend}
        isOldChat={isOldChat}
        disable={disable}
        setLoading={setLoadingMessages}
        selectedMedia={selectedMedia}
        addSelectedMedia={addSelectedMedia}
        beginDeviceMediaUploads={beginDeviceMediaUploads}
        completeDeviceMediaUpload={completeDeviceMediaUpload}
        failDeviceMediaUpload={failDeviceMediaUpload}
        removeSelectedMedia={removeSelectedMedia}
        msgList={msgList}
        lastReportID={lastReportID}
        handleValuesChange={handleValuesChange}
        onSendLocation={sendLocationMessage}
        parentNumber={parentNumber}
      />
    </div>
  );
};

export default Conversation;
